import { useMemo } from "react";
import { Stack, Typography } from "@mui/material";
import type { Control } from "react-hook-form";
import { RadioButtons } from "Components/Shared/Inputs/Form/RadioButtons";
import { DateFormInput } from "Components/Shared/Inputs/Form/DateFormInput";
import { Resources, useResource } from "Translations/Resources";
import type { CodeListItem } from "Models/ICodeListDto";
import type { CampaignFormData } from "./campaignFormSchema";

const RESOURCES = Resources.Admin.Campaigns.Form;

type Props = {
  control: Control<CampaignFormData>;
  isSubmitting: boolean;
  dateMode: "unlimited" | "from-to";
  onDateModeChange: (value: string) => void;
};

export const CampaignDatesSection = ({
  control,
  isSubmitting,
  dateMode,
  onDateModeChange,
}: Props) => {
  const { t } = useResource();

  const dateModeOptions: Array<CodeListItem> = useMemo(
    () => [
      {
        id: 1,
        code: "unlimited",
        name: t(RESOURCES.DateModeUnlimited),
        description: t(RESOURCES.DateModeUnlimitedDescription),
      },
      {
        id: 2,
        code: "from-to",
        name: t(RESOURCES.DateModeFromTo),
        description: t(RESOURCES.DateModeFromToDescription),
      },
    ],
    [t],
  );

  return (
    <Stack gap={3}>
      <Typography variant="h6" fontWeight={600}>
        {t(RESOURCES.CampaignValidity)}
      </Typography>

      <RadioButtons
        control={control}
        name="dateMode"
        codeList={dateModeOptions}
        disabled={isSubmitting}
        row
        justifyContent="flex-start"
        onAfterChange={onDateModeChange}
      />

      {dateMode === "from-to" && (
        <Stack gap={2} direction="row">
          <DateFormInput
            control={control}
            name="startDate"
            label={t(RESOURCES.StartDate)}
            disabled={isSubmitting}
            slotProps={{ textField: { fullWidth: true } }}
          />

          <DateFormInput
            control={control}
            name="endDate"
            label={t(RESOURCES.EndDate)}
            disabled={isSubmitting}
            slotProps={{ textField: { fullWidth: true } }}
          />
        </Stack>
      )}
    </Stack>
  );
};
